(() => {
  if (customElements.get('leslis-collection-scroll')) return;

  class LeslisCollectionScroll extends HTMLElement {
    connectedCallback() {
      this.generation = (this.generation || 0) + 1;
      const generation = this.generation;
      queueMicrotask(() => {
        if (this.isConnected && generation === this.generation) this.initialize();
      });
    }

    initialize() {
      this.grid = this.querySelector('ul#product-grid');
      this.controls = this.querySelector('[data-collection-scroll-controls]');
      this.button = this.querySelector('[data-collection-scroll-load]');
      this.status = this.querySelector('[data-collection-scroll-status]');
      this.fallback = this.querySelector('[data-collection-scroll-fallback]');
      this.sentinel = this.querySelector('[data-collection-scroll-sentinel]');
      this.scope = this.closest('collection-component');
      if (!this.grid || !this.controls || !this.button || !this.status || !this.fallback || !this.sentinel || !this.scope) return;
      this.page = Number(this.dataset.page);
      this.pages = Number(this.dataset.pages);
      this.pageParam = this.dataset.pageParam || 'page';
      if (!Number.isInteger(this.page) || !Number.isInteger(this.pages) || this.page < 1 || this.pages < this.page) return;
      this.context = new URL(location.href);
      this.contextKey = this.signature(this.context);
      this.documentKey = this.signature(this.context, true);
      this.nextUrl = this.dataset.nextUrl || '';
      this.ids = new Set(Array.from(this.grid.children, (card) => card.dataset.leslisCollectionProductId).filter(Boolean));
      this.invalidated = false;
      this.busy = false;
      this.emptyBatches = 0;
      this.autoPaused = false;
      this.moreText = this.button.textContent;
      this.onLoad = () => this.load(true);
      this.onFilter = (event) => {
        if (event.target.closest('facet-filters-form, facet-remove, .js-facet-remove')) this.invalidate();
      };
      this.onHistory = () => { if (this.signature(new URL(location.href), true) !== this.documentKey) this.invalidate(); };
      this.onButtonBlur = () => { if (!this.nextUrl) this.button.hidden = true; };
      this.button.addEventListener('click', this.onLoad);
      this.button.addEventListener('blur', this.onButtonBlur);
      this.scope.addEventListener('input', this.onFilter, true);
      this.scope.addEventListener('change', this.onFilter, true);
      this.scope.addEventListener('click', this.onFilterClick = (event) => {
        if (event.target.closest('facet-remove, .js-facet-remove')) this.invalidate();
      }, true);
      window.addEventListener('popstate', this.onHistory);
      if (typeof IntersectionObserver === 'function') {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting) && !this.autoPaused) this.load(false);
        }, { rootMargin: '0px 0px 480px 0px' });
      }
      this.setAttribute('data-enhanced', '');
      this.controls.hidden = !this.nextUrl;
      this.updateControls();
      this.observe();
    }

    disconnectedCallback() {
      this.generation = (this.generation || 0) + 1;
      this.controller?.abort();
      clearTimeout(this.timeout);
      this.observer?.disconnect();
      this.button?.removeEventListener('click', this.onLoad);
      this.button?.removeEventListener('blur', this.onButtonBlur);
      this.scope?.removeEventListener('input', this.onFilter, true);
      this.scope?.removeEventListener('change', this.onFilter, true);
      this.scope?.removeEventListener('click', this.onFilterClick, true);
      window.removeEventListener('popstate', this.onHistory);
    }

    signature(url, includePage = false) {
      const params = new URLSearchParams(Array.from(url.searchParams).filter(([name, value]) => !name.startsWith('filter.') || value !== ''));
      ['section_id', 'sections'].forEach((name) => params.delete(name));
      if (!includePage) params.delete(this.pageParam);
      return `${url.origin}${url.pathname}?${Array.from(params).sort(([a, x], [b, y]) => a.localeCompare(b) || x.localeCompare(y)).map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join('&')}`;
    }

    current() {
      return this.isConnected && !this.invalidated && this.signature(new URL(location.href), true) === this.documentKey &&
        !this.closest('.collection')?.classList.contains('loading');
    }

    invalidate() {
      this.invalidated = true;
      this.controller?.abort();
      this.observer?.disconnect();
    }

    observe() {
      if (this.nextUrl && !this.autoPaused && this.current()) this.observer?.observe(this.sentinel);
    }

    updateControls() {
      this.button.setAttribute('aria-disabled', String(this.busy || !this.nextUrl));
      this.button.textContent = this.nextUrl ? this.moreText : this.dataset.endText;
      this.button.hidden = !this.nextUrl && document.activeElement !== this.button;
      this.setAttribute('aria-busy', String(this.busy));
      if (this.nextUrl) {
        const url = new URL(this.context);
        ['section_id', 'sections'].forEach((name) => url.searchParams.delete(name));
        url.searchParams.set(this.pageParam, String(this.page + 1));
        url.hash = '';
        this.fallback.href = url.href;
      }
    }

    requestUrl() {
      const url = new URL(this.nextUrl, this.context);
      const preview = this.context.searchParams.get('preview_theme_id');
      if (preview && /^\d+$/.test(preview)) url.searchParams.set('preview_theme_id', preview);
      const previewBar = this.context.searchParams.get('pb');
      if (previewBar === '0' || previewBar === '1') url.searchParams.set('pb', previewBar);
      if (this.signature(url) !== this.contextKey) throw new Error('Collection context changed');
      const page = Number(url.searchParams.get(this.pageParam));
      if (!Number.isInteger(page) || page !== this.page + 1 || page > this.pages) throw new Error('Invalid next page');
      url.hash = '';
      url.searchParams.set('section_id', this.dataset.sectionId);
      return { url, page };
    }

    async load(manual) {
      if (this.busy || !this.nextUrl || !this.current()) return;
      if (manual) { this.autoPaused = false; this.emptyBatches = 0; }
      this.busy = true;
      this.observer?.unobserve(this.sentinel);
      this.fallback.hidden = true;
      this.status.textContent = this.dataset.loadingText;
      this.updateControls();
      const generation = this.generation;
      const controller = new AbortController();
      this.controller = controller;
      const timeout = setTimeout(() => controller.abort(), 12000);
      this.timeout = timeout;
      try {
        const { url, page } = this.requestUrl();
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error('Collection unavailable');
        const html = await response.text();
        if (generation !== this.generation || !this.current()) return;
        if (controller.signal.aborted) throw new Error('Collection request expired');
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source = parsed.getElementById('ProductGridContainer')?.querySelector('leslis-collection-scroll');
        const grid = source?.querySelector('ul#product-grid');
        if (!grid || source.dataset.sectionId !== this.dataset.sectionId || Number(source.dataset.page) !== page) throw new Error('Invalid collection section');
        const cards = Array.from(grid.children);
        if (cards.some((card) => card.tagName !== 'LI' || !/^\d+$/.test(card.dataset.leslisCollectionProductId || ''))) throw new Error('Invalid product cards');
        const fragment = document.createDocumentFragment();
        let added = 0;
        for (const card of cards) {
          const id = card.dataset.leslisCollectionProductId;
          if (this.ids.has(id)) continue;
          this.ids.add(id);
          card.querySelectorAll('img').forEach((image) => { image.loading = 'lazy'; image.decoding = 'async'; });
          card.classList.add('scroll-trigger--cancel');
          fragment.append(card);
          added += 1;
        }
        this.grid.append(fragment);
        this.page = page;
        this.dataset.page = String(page);
        this.nextUrl = page < this.pages ? source.dataset.nextUrl || '' : '';
        this.dataset.nextUrl = this.nextUrl;
        this.emptyBatches = added ? 0 : this.emptyBatches + 1;
        this.autoPaused = this.emptyBatches >= 3;
        this.status.textContent = this.nextUrl ? added ? this.dataset.loadedText : '' : this.dataset.endText;
        if (!this.nextUrl) this.observer?.disconnect();
      } catch {
        if (generation !== this.generation || !this.current()) return;
        this.autoPaused = true;
        this.status.textContent = this.dataset.errorText;
        this.fallback.hidden = false;
      } finally {
        clearTimeout(timeout);
        if (generation === this.generation && this.isConnected) {
          this.busy = false;
          this.updateControls();
          this.observe();
        }
      }
    }
  }

  customElements.define('leslis-collection-scroll', LeslisCollectionScroll);
})();
